// ============================================
// Library Scoring Engine
//
// Aggregates individual document scores and
// cross-document consistency into a unified
// Brand Legal Readiness score.
//
// Combined = (avg individual × 0.6) + (cross-doc × 0.4)
// Launch threshold: >= 92 AND zero critical conflicts
// ============================================

import type {
  DocDocument, DocTemplate, BrandConfig,
  LibraryScore, CrossDocScore, GCConflict,
} from '../../types/docops';
import { ENTITY_REGISTRY } from '../../data/docops/entityRegistry';
import { DEFINED_TERMS_REGISTRY } from '../../data/docops/definedTermsRegistry';
import { PROVISION_ALIGNMENT_MAP } from '../../data/docops/provisionAlignmentMap';
import { COMPLETED_STATUSES } from '../../utils/docopsPipeline';

// ── INDIVIDUAL SCORE AGGREGATION ──

/**
 * Compute average individual quality gate score
 * across all generated documents.
 */
export function computeAvgIndividualScore(documents: DocDocument[]): number {
  const scored = documents.filter(d =>
    d.auditScore !== undefined && d.status !== 'not_started',
  );
  if (scored.length === 0) return 0;
  const sum = scored.reduce((acc, d) => acc + (d.auditScore ?? 0), 0);
  return Math.round(sum / scored.length);
}

// ── ENTITY CONSISTENCY CHECK ──

// Map entity IDs to BrandConfig fields so we can dynamically add
// actual config values (e.g. "Dr. Christopher Calapai, DO") as valid references
const ENTITY_CONFIG_FIELDS: Record<string, (keyof BrandConfig)[]> = {
  medicalDirector: ['medicalDirectorName'],
  professionalCorporation: ['pcEntityName'],
  operatingCompany: ['brandEntity', 'brandName'],
  trustEntity: ['trustName'],
  telehealthProvider: ['telehealthNetwork'],
  paymentProcessor: ['paymentProcessor'],
};

/**
 * Check that entity references in generated documents
 * use only valid reference names from the entity registry.
 * Also checks actual brand config values (e.g. the real Medical Director name).
 */
export function checkEntityConsistency(
  documents: DocDocument[],
  templates: DocTemplate[],
  config?: BrandConfig,
): { score: number; conflicts: GCConflict[] } {
  const conflicts: GCConflict[] = [];
  const generated = documents.filter(d => d.content && d.status !== 'not_started');

  // Check each entity against each document it should appear in
  let totalChecks = 0;
  let passedChecks = 0;

  for (const entity of ENTITY_REGISTRY.entities) {
    // Build expanded valid references: registry refs + actual config values
    const expandedRefs = [...entity.validReferences];
    if (config) {
      const configFields = ENTITY_CONFIG_FIELDS[entity.id];
      if (configFields) {
        for (const field of configFields) {
          const val = config[field];
          if (typeof val === 'string' && val.trim() && !val.startsWith('{{')) {
            expandedRefs.push(val.trim());
          }
        }
      }
    }
    // Also add the role name without article (e.g. "Medical Director" from "the Medical Director")
    for (const ref of entity.validReferences) {
      const withoutArticle = ref.replace(/^the\s+/i, '');
      if (withoutArticle !== ref && !expandedRefs.includes(withoutArticle)) {
        expandedRefs.push(withoutArticle);
      }
    }

    for (const doc of generated) {
      const tpl = templates.find(t => t.id === doc.templateId);
      if (!tpl || !entity.appearsIn.includes(doc.templateId)) continue;

      totalChecks++;
      const content = doc.content.toLowerCase();

      // Check if at least one valid reference appears
      const hasValidRef = expandedRefs.some(ref =>
        content.includes(ref.toLowerCase()),
      );

      if (hasValidRef) {
        passedChecks++;
      } else {
        conflicts.push({
          id: `entity-${entity.id}-${doc.id}`,
          severity: 'high',
          type: 'entity',
          documentA: doc.templateId,
          sectionA: 'Full document',
          documentB: '',
          sectionB: '',
          description: `Entity "${entity.role}" (${entity.legalName || entity.id}) not found in ${doc.title}. Expected in documents of this type.`,
          recommendation: `Add a reference using one of: ${expandedRefs.filter(r => !r.startsWith('{{')).join(', ')}`,
          autoFixAvailable: false,
          status: 'open',
        });
      }
    }
  }

  const score = totalChecks > 0 ? Math.round((passedChecks / totalChecks) * 100) : 100;
  return { score, conflicts };
}

// ── DEFINED TERMS CONSISTENCY CHECK ──

/**
 * Build search variants for a defined term.
 * Handles compound terms like "Tier 1 / AI-Powered Lab Analysis"
 * by splitting on " / " and also checking each part individually.
 * Also handles parenthetical terms like "Protected Health Information (PHI)".
 */
function termSearchVariants(term: string): string[] {
  const variants: string[] = [term.toLowerCase()];

  // Compound terms: "Tier 1 / AI-Powered Lab Analysis" → also match "Tier 1" and "AI-Powered Lab Analysis"
  if (term.includes(' / ')) {
    for (const part of term.split(' / ')) {
      const trimmed = part.trim().toLowerCase();
      if (trimmed.length >= 4) variants.push(trimmed);
    }
  }

  // Parenthetical: "Protected Health Information (PHI)" → also match "PHI" and "Protected Health Information"
  const parenMatch = term.match(/^(.+?)\s*\(([^)]+)\)$/);
  if (parenMatch) {
    variants.push(parenMatch[1].trim().toLowerCase());
    variants.push(parenMatch[2].trim().toLowerCase());
  }

  return variants;
}

/**
 * Check that defined terms appear in documents where expected.
 */
export function checkDefinedTerms(
  documents: DocDocument[],
): { score: number; conflicts: GCConflict[] } {
  const conflicts: GCConflict[] = [];
  const generated = documents.filter(d => d.content && d.status !== 'not_started');

  let totalChecks = 0;
  let passedChecks = 0;

  for (const term of DEFINED_TERMS_REGISTRY.terms) {
    const variants = termSearchVariants(term.term);

    for (const doc of generated) {
      if (!term.usedIn.includes(doc.templateId)) continue;

      totalChecks++;
      const content = doc.content.toLowerCase();

      // Pass if ANY variant appears in the document
      const found = variants.some(v => content.includes(v));

      if (found) {
        passedChecks++;
      } else {
        conflicts.push({
          id: `term-${term.term.replace(/\s+/g, '-').toLowerCase()}-${doc.id}`,
          severity: 'medium',
          type: 'defined_term',
          documentA: doc.templateId,
          sectionA: 'Full document',
          documentB: term.definedIn,
          sectionB: `Definition of "${term.term}"`,
          description: `Defined term "${term.term}" expected but not found in ${doc.title}.`,
          recommendation: `Add reference to "${term.term}" or cross-reference "as defined in the Terms of Service."`,
          autoFixAvailable: false,
          status: 'open',
        });
      }
    }
  }

  const score = totalChecks > 0 ? Math.round((passedChecks / totalChecks) * 100) : 100;
  return { score, conflicts };
}

// ── CROSS-REFERENCE INTEGRITY CHECK ──

/**
 * Build title search variants for cross-reference checking.
 * Handles common patterns like "LLC Operating Agreement" → also match "Operating Agreement",
 * "BAA: LLC ↔ PC" → "Business Associate Agreement", etc.
 */
function titleSearchVariants(title: string): string[] {
  const variants: string[] = [title.toLowerCase()];

  // Strip common legal prefixes: "LLC ", "PC ", "BAA: "
  const stripped = title.replace(/^(LLC|PC|BAA:\s*)/i, '').trim();
  if (stripped !== title && stripped.length >= 4) variants.push(stripped.toLowerCase());

  // Strip parenthetical qualifiers: "Consumer Health Data Policy (MHMDA + CTDPA)"
  const noParens = title.replace(/\s*\([^)]+\)\s*$/, '').trim();
  if (noParens !== title && noParens.length >= 4) variants.push(noParens.toLowerCase());

  // BAA variants: "BAA: LLC ↔ PC" → "business associate agreement"
  if (title.toLowerCase().startsWith('baa')) {
    variants.push('business associate agreement');
    variants.push('baa');
  }

  // Common abbreviations for long titles
  if (title.toLowerCase().includes('notice of privacy practices')) {
    variants.push('notice of privacy practices');
    variants.push('npp');
  }
  if (title.toLowerCase().includes('terms of service')) {
    variants.push('terms of service');
    variants.push('terms');
  }
  if (title.toLowerCase().includes('privacy policy')) {
    variants.push('privacy policy');
  }
  if (title.toLowerCase().includes('operating agreement')) {
    variants.push('operating agreement');
  }
  if (title.toLowerCase().includes('management services agreement')) {
    variants.push('management services agreement');
    variants.push('msa');
  }
  if (title.toLowerCase().includes('medical director agreement')) {
    variants.push('medical director agreement');
  }
  if (title.toLowerCase().includes('ip license agreement')) {
    variants.push('ip license agreement');
    variants.push('license agreement');
  }

  return [...new Set(variants)];
}

/**
 * Check that documents with dependencies reference their
 * dependency documents (at minimum by title or common abbreviation).
 */
export function checkCrossReferences(
  documents: DocDocument[],
  templates: DocTemplate[],
): { score: number; conflicts: GCConflict[] } {
  const conflicts: GCConflict[] = [];
  const generated = documents.filter(d => d.content && d.status !== 'not_started');

  let totalChecks = 0;
  let passedChecks = 0;

  for (const doc of generated) {
    const tpl = templates.find(t => t.id === doc.templateId);
    if (!tpl?.dependencies?.length) continue;

    for (const depId of tpl.dependencies) {
      const depTpl = templates.find(t => t.id === depId);
      if (!depTpl) continue;

      totalChecks++;
      const content = doc.content.toLowerCase();
      const variants = titleSearchVariants(depTpl.title);

      // Pass if ANY title variant appears
      const found = variants.some(v => content.includes(v));

      if (found) {
        passedChecks++;
      } else {
        conflicts.push({
          id: `xref-${doc.id}-${depId}`,
          severity: 'medium',
          type: 'cross_reference',
          documentA: doc.templateId,
          sectionA: 'Full document',
          documentB: depId,
          sectionB: depTpl.title,
          description: `${doc.title} depends on "${depTpl.title}" but does not reference it.`,
          recommendation: `Add cross-reference to "${depTpl.title}" where relevant.`,
          autoFixAvailable: false,
          status: 'open',
        });
      }
    }
  }

  const score = totalChecks > 0 ? Math.round((passedChecks / totalChecks) * 100) : 100;
  return { score, conflicts };
}

// ── REGULATORY COMPLETENESS CHECK ──

/**
 * Check that all required documents exist based on jurisdictions
 * and enabled service tiers.
 */
export function checkRegulatoryCompleteness(
  documents: DocDocument[],
  config: BrandConfig,
): { score: number; conflicts: GCConflict[] } {
  const conflicts: GCConflict[] = [];
  const required: { templateId: string; reason: string }[] = [];

  // Always required
  required.push({ templateId: '1.1-terms-of-service', reason: 'Required for all platforms' });
  required.push({ templateId: '1.2-privacy-policy', reason: 'Required for all platforms' });

  // Jurisdiction-driven
  if (config.jurisdictions?.includes('WA')) {
    required.push({ templateId: '1.3-consumer-health-data-policy', reason: 'Required by WA MHMDA' });
  }

  // Tier-driven
  if (config.tiers?.tier2?.enabled) {
    required.push({ templateId: '1.7-telehealth-informed-consent', reason: 'Required for Tier 2 telehealth' });
    required.push({ templateId: '1.4-hipaa-npp', reason: 'Required for Tier 2 telehealth (HIPAA)' });
    required.push({ templateId: '5.1-baa-llc-pc', reason: 'Required for Tier 2 telehealth (BAA)' });
  }

  let totalChecks = required.length;
  let passedChecks = 0;

  for (const req of required) {
    const doc = documents.find(d => d.templateId === req.templateId);
    if (doc && doc.status !== 'not_started') {
      passedChecks++;
    } else {
      conflicts.push({
        id: `regulatory-missing-${req.templateId}`,
        severity: 'medium',
        type: 'regulatory',
        documentA: req.templateId,
        sectionA: '',
        documentB: '',
        sectionB: '',
        description: `Required document missing or not generated: ${req.templateId}. ${req.reason}.`,
        recommendation: `Generate this document before launch.`,
        autoFixAvailable: false,
        status: 'pending',
      });
    }
  }

  const score = totalChecks > 0 ? Math.round((passedChecks / totalChecks) * 100) : 100;
  return { score, conflicts };
}

// ── PROVISION ALIGNMENT CHECK ──

/**
 * Lightweight check: for each provision pair, verify both
 * documents exist and are generated. Full semantic alignment
 * requires AI (GC Agent Phase 2).
 */
export function checkProvisionAlignment(
  documents: DocDocument[],
): { score: number; conflicts: GCConflict[] } {
  const conflicts: GCConflict[] = [];
  const generated = documents.filter(d => d.content && d.status !== 'not_started');
  const generatedIds = new Set(generated.map(d => d.templateId));

  let totalChecks = 0;
  let passedChecks = 0;

  for (const pair of PROVISION_ALIGNMENT_MAP) {
    // Only check pairs where at least one document is generated
    const hasA = generatedIds.has(pair.documentA);
    const hasB = generatedIds.has(pair.documentB);

    if (!hasA && !hasB) continue; // Neither generated yet, skip

    totalChecks++;

    if (hasA && hasB) {
      // Both generated — count as provisionally aligned
      // (full semantic check requires GC Agent AI analysis)
      passedChecks++;
    } else {
      const missing = !hasA ? pair.documentA : pair.documentB;
      conflicts.push({
        id: `provision-${pair.id}`,
        severity: 'low',
        type: 'provision',
        documentA: pair.documentA,
        sectionA: pair.sectionA,
        documentB: pair.documentB,
        sectionB: pair.sectionB,
        description: `Provision pair "${pair.whatMustAlign}" cannot be verified — ${missing} not yet generated.`,
        recommendation: `Generate ${missing} to enable cross-document alignment check.`,
        autoFixAvailable: false,
        status: 'pending',
      });
    }
  }

  const score = totalChecks > 0 ? Math.round((passedChecks / totalChecks) * 100) : 100;
  return { score, conflicts };
}

// ── COMBINED LIBRARY SCORE ──

/**
 * Compute the full library score combining individual
 * document quality and cross-document consistency.
 */
export function computeLibraryScore(
  documents: DocDocument[],
  templates: DocTemplate[],
  config: BrandConfig,
): LibraryScore {
  const avgIndividual = computeAvgIndividualScore(documents);

  const entity = checkEntityConsistency(documents, templates, config);
  const terms = checkDefinedTerms(documents);
  const xref = checkCrossReferences(documents, templates);
  const regulatory = checkRegulatoryCompleteness(documents, config);
  const provisions = checkProvisionAlignment(documents);

  // Temporal consistency placeholder (requires date parsing — Phase 2)
  const temporalScore = 100;

  const overallConsistency = Math.round(
    (entity.score * 0.20) +
    (terms.score * 0.15) +
    (provisions.score * 0.25) +
    (xref.score * 0.10) +
    (regulatory.score * 0.20) +
    (temporalScore * 0.10),
  );

  const crossDocScore: CrossDocScore = {
    entityConsistency: entity.score,
    definedTermConsistency: terms.score,
    provisionAlignment: provisions.score,
    crossReferenceIntegrity: xref.score,
    regulatoryCompleteness: regulatory.score,
    temporalConsistency: temporalScore,
    overallConsistency,
  };

  const combinedScore = Math.round(
    (avgIndividual * 0.6) + (overallConsistency * 0.4),
  );

  // Collect all conflicts
  const allConflicts = [
    ...entity.conflicts,
    ...terms.conflicts,
    ...xref.conflicts,
    ...regulatory.conflicts,
    ...provisions.conflicts,
  ];

  const criticalConflicts = allConflicts.filter(c => c.severity === 'critical' && c.status !== 'pending').length;

  const generated = documents.filter(d => d.status !== 'not_started');
  const launchBlockers = documents.filter(d => d.launchBlocker);
  const launchBlockersComplete = launchBlockers.filter(d =>
    COMPLETED_STATUSES.has(d.status),
  ).length;

  return {
    avgIndividualScore: avgIndividual,
    crossDocScore,
    combinedScore,
    totalDocuments: documents.length,
    generatedDocuments: generated.length,
    launchBlockersComplete,
    launchBlockersTotal: launchBlockers.length,
    criticalConflicts,
    conflicts: allConflicts,
    passed: combinedScore >= 92 && criticalConflicts === 0,
    timestamp: new Date().toISOString(),
  };
}
